import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE as API } from "../lib/apiBase";

/**
 * Eric 2026-07-20 (iter162) — Hook custom extrait de VideoDetailPage.js
 * (refactor phase 2 : hooks).
 *
 * Encapsule TOUT le flow "Ré-analyser l'audio" :
 * 1. POST /speakers/reprocess (fire-and-forget côté backend depuis iter162)
 * 2. Polling GET /speakers toutes les 5s pour lire reprocess_status
 * 3. Termine sur "complete" (refresh speakerData) ou "failed" (toast erreur)
 * 4. Max 12 min de polling avant timeout local
 * 5. Résilient aux erreurs réseau transitoires pendant le poll
 * 6. Fallback legacy si l'API renvoie une réponse sync (avant redéploiement)
 *
 * @param {Object} params
 * @param {string} params.videoId
 * @param {string} params.lang - "fr" | "en"
 * @param {() => Promise<void>} params.reloadSpeakerData - Callback pour rafraîchir speakerData
 * @returns {{
 *   speakerReprocessing: boolean,
 *   startReprocess: (opts: {selectedSpeakers: string[], useDiarisation: boolean, useFacialId: boolean}) => Promise<void>
 * }}
 */
export const useSpeakerReprocess = ({ videoId, lang, reloadSpeakerData, startPolling }) => {
  const [speakerReprocessing, setSpeakerReprocessing] = useState(false);

  const startReprocess = useCallback(
    async ({ selectedSpeakers, useDiarisation, useFacialId }) => {
      setSpeakerReprocessing(true);
      // Bug 535 (Eric 2026-02-14) — Redémarre le polling pipeline dès le
      // clic pour que la barre horizontale bleue passe à « Diarisation
      // (AssemblyAI) » en temps réel (le backend set pipeline_stage=diarization
      // dès le POST).
      startPolling?.();
      const toastId = toast.loading(
        lang === "fr"
          ? "🎙️ Ré-analyse de l'audio en cours (diarisation AssemblyAI + reconnaissance visuelle)…"
          : "🎙️ Re-analyzing audio (AssemblyAI diarization + visual recognition)…",
        { duration: Infinity }
      );

      try {
        const resp = await axios.post(
          `${API}/api/videos/${videoId}/speakers/reprocess`,
          {
            selected_speakers: selectedSpeakers,
            use_diarisation: useDiarisation,
            use_facial_id: useFacialId,
          },
          { withCredentials: true, timeout: 30000 }
        );

        // Vincent B635 v2 (Eric 2026-08-01 SPEC STRICK PROD) — Rafraîchit
        // IMMÉDIATEMENT après la POST 200 (avant le polling toutes les 5 s).
        // Le backend a déjà reset `speaker_validation="pending"` +
        // `transcripts.speakers_validated=false` dans le pre-task update
        // (`_run_speakers_reprocess_task`). Sans ce reload immédiat, la
        // React state `video` conservait pendant 5 s à 5 min la valeur
        // pré-reprocess (`speaker_validation="validated"`) → les badges
        // « Validé » à côté du titre « Locuteurs détectés (N) » et
        // « Locuteurs validés » plus bas, + les couleurs teal des lignes,
        // restaient visibles alors que la re-diarisation était en cours.
        try {
          await reloadSpeakerData();
        } catch { /* non bloquant */ }

        const isAsync =
          resp?.data?.async === true ||
          resp?.data?.status === "started" ||
          resp?.data?.status === "already_running";

        if (isAsync) {
          // Polling toutes les 5s, max 12 min (144 tentatives)
          const maxAttempts = 144;
          let attempts = 0;

          const poll = async () => {
            attempts += 1;
            try {
              const { data: sd } = await axios.get(
                `${API}/api/videos/${videoId}/speakers`,
                { withCredentials: true, timeout: 30000 }
              );
              const st = sd?.reprocess_status;

              if (st === "complete") {
                await reloadSpeakerData();
                const r = sd?.reprocess_result || {};
                const nSpeakers = r.speakers_count ?? r.speakers_detected?.length ?? null;
                toast.success(
                  lang === "fr"
                    ? nSpeakers != null
                      ? `✅ Ré-analyse terminée — ${nSpeakers} locuteur(s) détecté(s).`
                      : "✅ Ré-analyse terminée."
                    : nSpeakers != null
                      ? `✅ Re-analysis complete — ${nSpeakers} speaker(s) detected.`
                      : "✅ Re-analysis complete.",
                  { id: toastId, duration: 8000 }
                );
                setSpeakerReprocessing(false);
                return;
              }

              if (st === "failed") {
                const err = sd?.reprocess_error || "Erreur inconnue";
                toast.error(
                  lang === "fr" ? `❌ Échec de la ré-analyse : ${err}` : `❌ Re-analysis failed: ${err}`,
                  { id: toastId, duration: 12000 }
                );
                setSpeakerReprocessing(false);
                return;
              }

              if (attempts >= maxAttempts) {
                toast.error(
                  lang === "fr"
                    ? "⏱️ Ré-analyse trop longue (>12 min). Vérifie l'onglet Locuteurs plus tard."
                    : "⏱️ Re-analysis timed out (>12 min). Check the Speakers tab later.",
                  { id: toastId, duration: 12000 }
                );
                setSpeakerReprocessing(false);
                return;
              }

              setTimeout(poll, 5000);
            } catch (pollErr) {
              // Erreurs réseau transitoires : on retente jusqu'à maxAttempts.
              if (attempts < maxAttempts) {
                setTimeout(poll, 5000);
              } else {
                toast.error(
                  lang === "fr"
                    ? `❌ Impossible de suivre l'avancement : ${pollErr.message}`
                    : `❌ Cannot track progress: ${pollErr.message}`,
                  { id: toastId, duration: 10000 }
                );
                setSpeakerReprocessing(false);
              }
            }
          };
          setTimeout(poll, 5000);
          return; // Le poll écrira lui-même setSpeakerReprocessing(false)
        }

        // Fallback : réponse legacy sync (avant redéploiement du fix async)
        await reloadSpeakerData();
        const nSpeakers = resp?.data?.speakers_count ?? resp?.data?.detected_speakers ?? null;
        toast.success(
          lang === "fr"
            ? nSpeakers != null
              ? `✅ Ré-analyse terminée — ${nSpeakers} locuteur(s) détecté(s). Passez en revue les identifications ci-dessous.`
              : "✅ Ré-analyse terminée. Passez en revue les identifications ci-dessous."
            : nSpeakers != null
              ? `✅ Re-analysis complete — ${nSpeakers} speaker(s) detected.`
              : "✅ Re-analysis complete.",
          { id: toastId, duration: 8000 }
        );
        setSpeakerReprocessing(false);
      } catch (e) {
        console.error(e);
        const detail = e.response?.data?.detail || e.message || "Erreur inconnue";
        toast.error(
          lang === "fr" ? `❌ Échec de la ré-analyse : ${detail}` : `❌ Re-analysis failed: ${detail}`,
          { id: toastId, duration: 10000 }
        );
        setSpeakerReprocessing(false);
      }
    },
    [videoId, lang, reloadSpeakerData, startPolling]
  );

  return { speakerReprocessing, startReprocess };
};

export default useSpeakerReprocess;
